import type { Calibration, ChunkInfo, Geometry, WorldRect } from '../geo/types';

export type Role = 'admin' | 'editor' | 'viewer';
export type Lang = 'sv' | 'en';
export type Status = 'idea' | 'proposed' | 'approved' | 'in_unity' | 'needs_update' | 'deprecated';
export type SyncState =
  'unlinked' | 'in_sync' | 'design_ahead' | 'unity_ahead' | 'conflict' | 'missing_in_unity';

interface BaseRecord {
  id: string;
  collectionId: string;
  collectionName: string;
  created: string;
  updated: string;
}

export interface UserRecord extends BaseRecord {
  email?: string;
  name: string;
  role: Role;
  lang?: Lang | '';
  avatar?: string;
}

export interface HeightmapMeta {
  minX: number;
  maxZ: number;
  step: number;
  width: number;
  height: number;
}

/** Dropdown sources exported from Unity (MapsEditorExporter catalog block). */
export type UnityCatalog = Partial<Record<string, { name?: string; id?: string; [k: string]: unknown }[]>>;

export interface MapRecord extends BaseRecord {
  slug: string;
  name: string;
  worldId: string;
  keyPrefix: string;
  worldRect: WorldRect;
  origin: { x: number; z: number } | null;
  chunkSize: number;
  gridX: number;
  gridZ: number;
  chunks: ChunkInfo[] | null;
  seaLevel: number;
  landExtent: WorldRect | null;
  defaultView: { x: number; z: number; zoom: number } | null;
  unityScenes: Record<string, string> | null;
  unityCatalog: UnityCatalog | null;
  heightmap: string;
  heightmapMeta: HeightmapMeta | null;
  lastImportAt: string;
  sortOrder: number;
  archived: boolean;
}

export interface BaseLayerRecord extends BaseRecord {
  map: string;
  name: string;
  kind: 'image' | 'tiles';
  image: string;
  tileUrlTemplate: string;
  widthPx: number;
  heightPx: number;
  calibration: Calibration | null;
  source: 'unity_render' | 'painted' | 'sketch' | 'other';
  opacity: number;
  sortOrder: number;
  visibleByDefault: boolean;
  notes: string;
}

export type PropValue = string | number | boolean | string[] | Record<string, unknown>[] | undefined;

export interface FeatureRecord extends BaseRecord {
  map: string;
  type: string;
  key: string;
  name: string;
  description: string;
  geometry: Geometry;
  properties: Record<string, PropValue>;
  designProps: Record<string, PropValue>;
  status: Status;
  showInGame: boolean;
  sortOrder: number;
  tags: string[];
  color: string;
  files: string[];
  rev: number;
  designHash: string;
  syncBaseHash: string;
  unityKey: string;
  createdBy: string;
  updatedBy: string;
  parent: string;
}

export interface CommentRecord extends BaseRecord {
  feature: string;
  dataRecord: string;
  map: string;
  author: string;
  body: string;
  resolved: boolean;
  files: string[];
}

export interface HistoryRecord extends BaseRecord {
  map: string;
  targetCollection: string;
  recordId: string;
  recordKey: string;
  action: 'create' | 'update' | 'delete' | 'status' | 'import' | 'restore' | 'rekey';
  actor: string;
  changedFields: string[] | null;
  before: Partial<FeatureRecord> | null;
  after: Partial<FeatureRecord> | null;
  summary: string;
}

export interface UnityObjectRecord extends BaseRecord {
  map: string;
  unityKey: string;
  type: string;
  component: string;
  unityId: string;
  parentKey: string;
  name: string;
  scene: string;
  hierarchyPath: string;
  geometry: Geometry | null;
  y: number;
  rotY: number;
  properties: Record<string, PropValue> | null;
  hash: string;
  missing: boolean;
}

export interface ImportPlan {
  dryRun: boolean;
  importId?: string;
  counts: Record<
    | 'objects'
    | 'adopted'
    | 'linked'
    | 'inSync'
    | 'fastForwarded'
    | 'designAhead'
    | 'conflicts'
    | 'missing'
    | 'skipped',
    number
  >;
  items: { unityKey: string; type: string; name: string; key: string | null; action: string }[];
  warnings: string[];
}

/** Mirrors syncState() in pocketbase/pb_hooks/lib/sync.js. */
export function syncStateOf(f: FeatureRecord, uo: UnityObjectRecord | undefined): SyncState {
  if (!f.unityKey) return 'unlinked';
  if (!uo || uo.missing) return 'missing_in_unity';
  if (f.designHash === uo.hash) return 'in_sync';
  if (!f.syncBaseHash) return 'conflict';
  if (uo.hash === f.syncBaseHash) return 'design_ahead';
  if (f.designHash === f.syncBaseHash) return 'unity_ahead';
  return 'conflict';
}
